import { GraphQLResult } from "@aws-amplify/api-graphql";

import {
  ComplianceReview,
  GetCurrentUserResidentsQuery,
  GetResidentBasicDataQuery,
  GetResidentQuery,
  GetResidentQueryVariables,
  GetResidentsQuery,
  GetResidentsQueryVariables,
  GetResidentsResponse,
  GetReviewDetailsQuery,
  GetReviewDetailsQueryVariables,
  SearchResidentsQueryVariables,
  SearchResidentsQuery,
  Resident,
  SetResidentPermanentExclusionMutation,
  SetResidentPermanentExclusionMutationVariables,
} from "@graphql/api";
import { api } from "@redux/api";
import { OperationName } from "@redux/utils/graphqlBaseOperation";
import { Tags } from "@redux/types";

export const residentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    searchResidents: build.query<
      GetResidentsResponse | undefined,
      SearchResidentsQueryVariables
    >({
      query: (data) => {
        const operationName: OperationName = "searchResidentsQuery";

        return {
          operationName,
          data,
        };
      },
      transformResponse: (response: GraphQLResult<SearchResidentsQuery>) =>
        response.data?.searchResidents,
      providesTags: (_res, _err, args) => [
        { type: Tags.ResidentsSearch, id: args.query },
      ],
    }),
    getResidents: build.query<
      GetResidentsResponse | undefined,
      GetResidentsQueryVariables
    >({
      query: (data) => {
        const operationName: OperationName = "getResidentsListItems";

        return {
          operationName,
          data,
        };
      },
      transformResponse: (response: GraphQLResult<GetResidentsQuery>) =>
        response.data?.getResidents,
      providesTags: (res, _err, args) =>
        res
          ? [
              ...res.items.map(({ id }) => ({ type: Tags.Resident, id })),
              { type: Tags.Resident, id: JSON.stringify(args) },
            ]
          : [{ type: Tags.Resident, id: JSON.stringify(args) }],
    }),
    getResident: build.query<Resident | undefined, GetResidentQueryVariables>({
      query: (data) => {
        const operationName: OperationName = "getResident";

        return {
          operationName,
          data,
        };
      },
      transformResponse: (response: GraphQLResult<GetResidentQuery>) =>
        response.data?.getResident,
      providesTags: (resident) =>
        resident ? [{ type: Tags.Resident, id: resident.id }] : [],
    }),
    setResidentPermanentExclusion: build.mutation<
      Resident,
      SetResidentPermanentExclusionMutationVariables
    >({
      query: (data) => {
        const operationName: OperationName = "setResidentPermanentExclusion";

        return {
          operationName,
          data,
        };
      },
      transformResponse: (
        response: GraphQLResult<SetResidentPermanentExclusionMutation>
      ) => response.data?.setResidentPermanentExclusion,
      invalidatesTags: (_result, error, { id }) =>
        error ? [] : [{ type: Tags.Resident, id }],
    }),
    getResidentBasicData: build.query<
      GetResidentBasicDataQuery["getResident"] | undefined,
      void
    >({
      query: (data) => {
        const operationName: OperationName = "getResidentBasicData";

        return {
          operationName,
          data,
        };
      },
      transformResponse: (response: GraphQLResult<GetResidentBasicDataQuery>) =>
        response.data?.getResident,
    }),
    getCurrentUserResidents: build.query<
      GetCurrentUserResidentsQuery["getCurrentUserResidents"],
      void
    >({
      query: (data) => {
        const operationName: OperationName = "getCurrentUserResidents";

        return {
          operationName,
          data,
        };
      },
      transformResponse: (
        response: GraphQLResult<GetCurrentUserResidentsQuery>
      ) => response.data?.getCurrentUserResidents ?? [],
      providesTags: (residents) =>
        residents
          ? residents.map(({ id }) => ({ type: Tags.Resident, id }))
          : [],
    }),
    getReviewDetails: build.query<
      ComplianceReview | undefined,
      GetReviewDetailsQueryVariables
    >({
      query: (data) => {
        const operationName: OperationName = "getReviewDetails";

        return {
          operationName,
          data,
        };
      },
      transformResponse: (response: GraphQLResult<GetReviewDetailsQuery>) =>
        response.data?.getReviewDetails,
      providesTags: (review, _, args) =>
        review ? [{ type: Tags.Review, id: args.id }] : [],
    }),
  }),
});

export const {
  useGetResidentsQuery,
  useGetCurrentUserResidentsQuery,
  useGetResidentBasicDataQuery,
  useGetResidentQuery,
  useSetResidentPermanentExclusionMutation,
  useGetReviewDetailsQuery,
  useLazySearchResidentsQuery,
} = residentsApi;
